import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { certificationApi } from '@/services/certification';
import { useClinicPortalServiceType } from './clinicPortalServiceType';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import type {
  CopyCertificationPayload,
  CopyDataResponse,
  CertificationDetailResponse,
  CertificationListItem,
  ComparedPatient,
  DuplicateSearchResponse,
  FormDataForComparison,
  InsurerData,
} from '@/services/certification/types';

const getCurrentServiceType = () => {
  if (typeof localStorage === 'undefined') return SERVICE_TYPE_A2;

  const serviceType = localStorage.getItem('service_type')?.trim().toUpperCase();
  return serviceType === SERVICE_TYPE_R2 ? SERVICE_TYPE_R2 : SERVICE_TYPE_A2;
};

// ==================== Query Keys ====================

export const CERTIFICATION_QUERY_KEYS = {
  list: () => ['certification', 'list'] as const,
  detail: (id: number) => ['certification', 'detail', id] as const,
  insurer: (insurerNumber: string) => ['certification', 'insurer', insurerNumber] as const,
  duplicatePatients: (ids: number[]) => ['certification', 'duplicatePatients', ids] as const,
} as const;

// ==================== Result Types ====================

export type CertificationCopyResult = {
  redirectUrl: string;
  data: CopyDataResponse;
};

export type SubmitCertificationResult =
  | { hasDuplicates: true; duplicateIds: number[] }
  | { hasDuplicates: false; redirectUrl: string; data: CopyDataResponse };

// ==================== List & Detail ====================

/**
 * 資格情報一覧を取得 (date_from / date_to optional filter)
 * @param params - date_from and date_to filters
 * @param enabled - whether to enable the query
 */
export function useCertificationList(
  params: { date_from?: string; date_to?: string } | undefined,
  enabled = false
) {
  // GET /patients/certification/list is scoped by X-Service-Type
  // (PatientCertificationController@list). Read it from the theme rather than the plain
  // localStorage getter above, so the list re-renders and refetches on an A<->R toggle. (HIT-11387)
  const serviceType = useClinicPortalServiceType();

  return useQuery<CertificationListItem[], Error>({
    queryKey: [...CERTIFICATION_QUERY_KEYS.list(), params?.date_from, params?.date_to, serviceType],
    queryFn: () => certificationApi.getCertificationList(params),
    enabled,
  });
}

/**
 * 資格情報詳細をIDで取得
 */
export function useCertificationDetail(certificationId: number, enabled = false) {
  const serviceType = getCurrentServiceType();

  return useQuery<CertificationDetailResponse, Error>({
    queryKey: [...CERTIFICATION_QUERY_KEYS.detail(certificationId), serviceType],
    queryFn: () => certificationApi.getCertificationDetail(certificationId),
    enabled: enabled && certificationId > 0,
  });
}

// ==================== Search & Copy ====================

/**
 * 保険者番号で保険者を検索（資格確認画面用）
 */
export function useInsurerCertificationSearch() {
  return useMutation<InsurerData, Error, string>({
    mutationFn: (insurerNumber: string) => certificationApi.searchInsurers(insurerNumber),
  });
}

/**
 * 重複患者検索（生年月日・性別で検索）
 */
export function useDuplicatePatientSearch() {
  return useMutation<DuplicateSearchResponse, Error, { sexId: number; dateOfBirth: string }>({
    mutationFn: ({ sexId, dateOfBirth }) =>
      certificationApi.searchDuplicatePatients(sexId, dateOfBirth),
  });
}

/**
 * 重複患者モーダル用 — 比較データ付きで患者一覧を取得
 */
export function useDuplicatePatients(
  patientIds: number[],
  formData: FormDataForComparison | undefined,
  enabled = false
) {
  return useQuery<ComparedPatient[], Error>({
    queryKey: [...CERTIFICATION_QUERY_KEYS.duplicatePatients(patientIds), formData],
    queryFn: () => certificationApi.getDuplicatePatients(patientIds, formData),
    enabled: enabled && patientIds.length > 0,
  });
}

/**
 * 資格確認データを患者編集画面へコピー（API呼び出し）
 */
export function useCopyCertificationData() {
  const queryClient = useQueryClient();

  return useMutation<
    CopyDataResponse,
    Error,
    { payload: CopyCertificationPayload; patientId?: number }
  >({
    mutationFn: ({ payload, patientId = 0 }) =>
      certificationApi.copyCertificationData(payload, patientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
}

// ==================== Submit Flow ====================

/**
 * 保険証画面へコピー時の一連フロー用フック
 */
export function useCertificationSubmit() {
  const duplicateSearch = useDuplicatePatientSearch();
  const copyData = useCopyCertificationData();

  const submitCertification = async (
    formData: CopyCertificationPayload,
    sexId: number,
    dateOfBirth: string
  ): Promise<SubmitCertificationResult> => {
    const searchResult = await duplicateSearch.mutateAsync({ sexId, dateOfBirth });

    if (searchResult.response && searchResult.ids.length > 0) {
      return { hasDuplicates: true, duplicateIds: searchResult.ids };
    }

    const copyResult = await copyData.mutateAsync({ payload: formData, patientId: 0 });
    return {
      hasDuplicates: false,
      redirectUrl: copyResult.redirect_url ?? '/patients/edit',
      data: copyResult,
    };
  };

  /** Copy to existing patient — overwrite (update_type=0) */
  const copyToExistingPatient = async (
    formData: CopyCertificationPayload,
    patientId: number
  ): Promise<CertificationCopyResult> => {
    const result = await copyData.mutateAsync({
      payload: { ...formData, update_type: 0 },
      patientId,
    });
    return {
      redirectUrl: result.redirect_url ?? `/patients/edit/${patientId}`,
      data: result,
    };
  };

  /** Copy to existing patient — create history (update_type=1) */
  const copyWithHistory = async (
    formData: CopyCertificationPayload,
    patientId: number
  ): Promise<CertificationCopyResult> => {
    const result = await copyData.mutateAsync({
      payload: { ...formData, update_type: 1 },
      patientId,
    });
    return {
      redirectUrl: result.redirect_url ?? `/patients/edit/${patientId}`,
      data: result,
    };
  };

  /** Copy as new patient (patient_id=0) */
  const copyToNewPatient = async (
    formData: CopyCertificationPayload
  ): Promise<CertificationCopyResult> => {
    const result = await copyData.mutateAsync({ payload: formData, patientId: 0 });
    return {
      redirectUrl: result.redirect_url ?? '/patients/edit',
      data: result,
    };
  };

  return {
    submitCertification,
    copyToExistingPatient,
    copyWithHistory,
    copyToNewPatient,
    isLoading: duplicateSearch.isPending || copyData.isPending,
    error: duplicateSearch.error ?? copyData.error,
  };
}
